import { useEffect, useState, useRef } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import api, { formatApiError, API_BASE } from "../lib/api";
import { formatDateUS, formatPhoneUS, displayPhone } from "../lib/format";
import PaymentInstructionsEditor from "../components/PaymentInstructionsEditor";
import PaymentInstructionsView from "../components/PaymentInstructionsView";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Card } from "../components/ui/card";
import { ArrowLeft, FileText, Handshake, Brain, Envelope, Paperclip, Copy, Sparkle, DownloadSimple, PencilSimpleLine, Signature, UploadSimple, Gavel, AddressBook, ChatCircleDots, Trash, MagnifyingGlass, PaperPlaneTilt, ArrowsClockwise, CheckCircle, XCircle, Clock as ClockIcon, Printer, FileDoc } from "@phosphor-icons/react";
import { toast } from "sonner";
import SignatureModal from "../components/SignatureModal";
import { generateLetterPdf, downloadPdf } from "../lib/pdf";
import { printText, downloadDocx, downloadDocxMulti } from "../lib/docExport";
import HardshipWorksheet from "../components/HardshipWorksheet";
import CourtListenerSearch from "../components/CourtListenerSearch";
import StateCourtPanel, { STATE_CONFIG } from "../components/StateCourtPanel";
import PayForDeletionModal from "../components/PayForDeletionModal";
import CaseAddressPrompt from "../components/CaseAddressPrompt";
import CaseAuditTimeline from "../components/CaseAuditTimeline";
import AutoEscalationToggle from "../components/AutoEscalationToggle";
import MailLetterDialog from "../components/MailLetterDialog";
import EditAIOutputDialog from "../components/EditAIOutputDialog";
import DateField from "../components/DateField";

const STATUS_LIST = ["new","investigating","disputing","negotiating","settled","closed"];

const MODES = [
    { key: "case_analysis", title: "Case Analysis", desc: "Plain-English breakdown + next admin steps you can take.", icon: Brain, bg: "bg-sky-100 hover:bg-sky-200", activeBg: "bg-sky-200", border: "border-sky-300", iconColor: "text-sky-700" },
    { key: "dispute_letter", title: "Validation Letter", desc: "Letter we draft — you sign and mail to the creditor.", icon: FileText, bg: "bg-fuchsia-100 hover:bg-fuchsia-200", activeBg: "bg-fuchsia-200", border: "border-fuchsia-300", iconColor: "text-fuchsia-700" },
    { key: "settlement_strategy", title: "Negotiation Worksheet", desc: "Tiered offers, scripts and talking points for your calls.", icon: Handshake, bg: "bg-orange-100 hover:bg-orange-200", activeBg: "bg-orange-200", border: "border-orange-300", iconColor: "text-orange-700" },
    { key: "settlement_letter", title: "Settlement Offer Letter", desc: "Settlement letter we draft — you sign and send.", icon: Envelope, bg: "bg-teal-100 hover:bg-teal-200", activeBg: "bg-teal-200", border: "border-teal-300", iconColor: "text-teal-700" },
];

const MODE_TITLES = Object.fromEntries(MODES.map((m) => [m.key, m.title]));

const PDF_TITLES = {
    case_analysis: "Case Analysis",
    dispute_letter: "Dispute & Validation Letter",
    settlement_strategy: "Settlement Strategy",
    settlement_letter: "Settlement Offer Letter",
};

export default function CaseDetail() {
    const { id } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const [cse, setCse] = useState(null);
    const [loading, setLoading] = useState(true);
    const [payPolling, setPayPolling] = useState(false);

    // AI panel state
    const [mode, setMode] = useState("case_analysis");
    const [extra, setExtra] = useState("");
    const [streaming, setStreaming] = useState(false);
    const [aiText, setAiText] = useState("");

    // Document state
    const [docName, setDocName] = useState("");
    const [docContent, setDocContent] = useState("");
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);

    // Signature state
    const [sigOpen, setSigOpen] = useState(false);
    const [pendingExport, setPendingExport] = useState(null); // {title, body}

    // Hardship narrative inclusion toggle (only meaningful when narrative exists)
    const [includeHardship, setIncludeHardship] = useState(true);

    // Court record state
    const [court, setCourt] = useState({ court_name: "", case_caption: "", docket_number: "", filed_date: "", judge: "", parties: "", raw_text: "" });
    const [savingCourt, setSavingCourt] = useState(false);
    const [cdStateCode, setCdStateCode] = useState("NC");
    const [explanation, setExplanation] = useState("");
    const [explaining, setExplaining] = useState(false);

    // Contacts state
    const [contactForm, setContactForm] = useState({ role: "creditor", name: "", organization: "", address: "", phone: "", email: "", bar_number: "", notes: "" });
    const [savingContact, setSavingContact] = useState(false);
    const [researchTarget, setResearchTarget] = useState("creditor");
    const [researchKnown, setResearchKnown] = useState("");
    const [researchOut, setResearchOut] = useState("");
    const [researching, setResearching] = useState(false);

    // Coach state
    const [coachMessages, setCoachMessages] = useState([]);
    const [coachInput, setCoachInput] = useState("");
    const [coachLoading, setCoachLoading] = useState(false);

    // Offers state
    const [offers, setOffers] = useState([]);
    const [pfdOpen, setPfdOpen] = useState(false);
    const [mailOpen, setMailOpen] = useState(false);
    const [mailingOutput, setMailingOutput] = useState(null);
    const [mailings, setMailings] = useState([]);
    const [editOpen, setEditOpen] = useState(false);
    const [editOutput, setEditOutput] = useState(null);
    const [deleteOutput, setDeleteOutput] = useState(null);
    const [deletingOutput, setDeletingOutput] = useState(false);
    const [regenId, setRegenId] = useState(null);
    const [savedSignature, setSavedSignature] = useState(null);
    const [sigManagerOpen, setSigManagerOpen] = useState(false);
    const openMailDialog = (o) => { setMailingOutput(o); setMailOpen(true); };
    const openEditDialog = (o) => { setEditOutput(o); setEditOpen(true); };
    const regenerate = async (o) => {
        if (!o?.mode) return;
        setRegenId(o.id);
        try {
            await api.post("/ai/generate-sync", { case_id: id, mode: o.mode });
            toast.success("Fresh AI output added to history");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setRegenId(null);
        }
    };
    const confirmDeleteOutput = async () => {
        if (!deleteOutput) return;
        setDeletingOutput(true);
        try {
            await api.delete(`/cases/${id}/ai-outputs/${deleteOutput.id}`);
            toast.success("Letter deleted");
            setDeleteOutput(null);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setDeletingOutput(false);
        }
    };

    // Audit state
    const [auditLog, setAuditLog] = useState([]);

    const load = async () => {
        try {
            const { data } = await api.get(`/cases/${id}`);
            setCse(data);
            if (data.court_record) {
                setCourt({
                    court_name: data.court_record.court_name || "",
                    case_caption: data.court_record.case_caption || "",
                    docket_number: data.court_record.docket_number || "",
                    filed_date: data.court_record.filed_date || "",
                    judge: data.court_record.judge || "",
                    parties: data.court_record.parties || "",
                    raw_text: data.court_record.raw_text || "",
                });
            }
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, [id]);

    useEffect(() => {
        api.get(`/coach/messages/${id}`).then((r) => setCoachMessages(r.data)).catch(() => {});
    }, [id]);

    const loadOffers = () => {
        api.get(`/offers`, { params: { case_id: id } }).then((r) => setOffers(r.data)).catch(() => {});
    };
    const loadMailings = () => {
        api.get(`/lob/mailings`, { params: { case_id: id } }).then((r) => setMailings(r.data)).catch(() => {});
    };
    useEffect(() => { loadOffers(); loadMailings(); /* eslint-disable-next-line */ }, [id]);
    useEffect(() => {
        api.get("/user/signature").then((r) => setSavedSignature(r.data.data_url || null)).catch(() => {});
    }, []);

    // After Stripe redirect: poll mail-status until the letter is sent or the user gives up.
    useEffect(() => {
        const paidSession = searchParams.get("letter_paid");
        const cancelled = searchParams.get("letter_cancelled");
        if (cancelled) {
            toast.info("Mailing cancelled — no charge.");
            searchParams.delete("letter_cancelled");
            setSearchParams(searchParams, { replace: true });
            return;
        }
        if (!paidSession) return;
        setPayPolling(true);
        let cancelledPoll = false;
        let attempts = 0;
        const tick = async () => {
            if (cancelledPoll) return;
            attempts += 1;
            try {
                const { data } = await api.get(`/lob/mail-status/${paidSession}`);
                if (data.letter_sent && data.mailing_id) {
                    toast.success("Payment confirmed — letter submitted to USPS.");
                    setPayPolling(false);
                    searchParams.delete("letter_paid");
                    setSearchParams(searchParams, { replace: true });
                    loadMailings();
                    return;
                }
                if (data.letter_send_error) {
                    toast.error(`Payment received but Lob rejected the letter: ${data.letter_send_error}`);
                    setPayPolling(false);
                    return;
                }
                if (data.payment_status === "expired") {
                    toast.error("Checkout session expired without payment.");
                    setPayPolling(false);
                    searchParams.delete("letter_paid");
                    setSearchParams(searchParams, { replace: true });
                    return;
                }
                if (attempts >= 30) {
                    toast.warning("Still confirming payment — refresh in a moment.");
                    setPayPolling(false);
                    return;
                }
                setTimeout(tick, 2000);
            } catch (e) {
                setPayPolling(false);
                toast.error(formatApiError(e));
            }
        };
        tick();
        return () => { cancelledPoll = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams.get("letter_paid"), searchParams.get("letter_cancelled")]);

    const loadAudit = () => {
        api.get(`/cases/${id}/audit`).then((r) => setAuditLog(r.data)).catch(() => {});
    };
    useEffect(() => { loadAudit(); /* eslint-disable-next-line */ }, [id]);

    const updateStatus = async (status) => {
        try {
            const { data } = await api.patch(`/cases/${id}`, { status });
            setCse(data);
            toast.success(`Status → ${status}`);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const runAI = async () => {
        setStreaming(true);
        setAiText("");
        const token = localStorage.getItem("ll_token");
        try {
            const res = await fetch(`${API_BASE}/ai/generate`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify({ case_id: id, mode, extra_context: extra || null, include_hardship: includeHardship }),
            });
            if (!res.ok || !res.body) {
                const txt = await res.text();
                throw new Error(txt || "AI request failed");
            }
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            while (true) {
                const { value, done } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                // SSE format: data: <chunk>\n\n
                const lines = buffer.split("\n\n");
                buffer = lines.pop() || "";
                for (const line of lines) {
                    if (!line.startsWith("data:")) continue;
                    const payload = line.slice(5).replace(/^ /, "");
                    if (payload === "[DONE]") continue;
                    if (payload.startsWith("[ERROR]")) {
                        toast.error(payload);
                        continue;
                    }
                    setAiText((p) => p + payload);
                }
            }
            toast.success("AI output saved to case");
            load();
        } catch (e) {
            toast.error(e.message || "AI failed");
        } finally {
            setStreaming(false);
        }
    };

    const addDocument = async (e) => {
        e.preventDefault();
        if (!docName.trim() || !docContent.trim()) return;
        try {
            await api.post("/documents", { case_id: id, name: docName, content: docContent });
            toast.success("Document attached");
            setDocName(""); setDocContent("");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const uploadFile = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const form = new FormData();
            form.append("case_id", id);
            form.append("file", file);
            const token = localStorage.getItem("ll_token");
            const res = await fetch(`${API_BASE}/documents/upload`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: form,
            });
            if (!res.ok) throw new Error(await res.text());
            toast.success("Document uploaded & analysed by Gemini");
            if (fileRef.current) fileRef.current.value = "";
            load();
        } catch (e) { toast.error(e.message || "Upload failed"); }
        finally { setUploading(false); }
    };


    const refundMailing = async (mailing) => {
        const reason = window.prompt(
            `Refund this mailing?\n\n` +
            `Postage class: ${mailing.postage_class.replace(/_/g, " ")}\n` +
            `Cost: $${(mailing.cost_estimate_usd || 0).toFixed(2)}\n` +
            `To: ${mailing.to_summary}\n\n` +
            `We will first try to cancel the letter with Lob (refunds postage automatically ` +
            `if the letter has not yet printed). If Lob refuses (already printed), we will ` +
            `credit the postage back to your account balance instead.\n\n` +
            `Enter a short reason (required, will be logged):`,
            "",
        );
        if (reason === null) return;
        const trimmed = reason.trim();
        if (trimmed.length < 3) {
            toast.error("Please enter a reason (at least 3 characters).");
            return;
        }
        try {
            const { data } = await api.post(`/lob/mailings/${mailing.id}/refund`, { reason: trimmed });
            const rs = data.refund_status;
            if (rs === "lob_cancelled") {
                toast.success(`Lob cancelled the letter — postage refunded directly.`);
            } else if (rs === "credited") {
                toast.success(`Credited $${((data.refund_amount_cents || 0) / 100).toFixed(2)} back to your balance.`);
            } else {
                toast.warning(`Refund status: ${rs}. Check the audit log for details.`);
            }
            // Refresh mailings list
            const list = await api.get("/lob/mailings", { params: { case_id: id } });
            setMailings(list.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Refund failed");
        }
    };

    const exportPdf = (content, modeKey, withSignature = false) => {
        if (!content) return;
        const title = PDF_TITLES[modeKey] || "LienLift Direct Document";
        const meta = `Case: ${cse.title}  ·  Creditor: ${cse.creditor_name}`;
        if (withSignature) {
            // Auto-apply saved signature if user has one — skip the modal
            if (savedSignature) {
                const doc = generateLetterPdf({ title, body: content, meta, signatureDataUrl: savedSignature });
                downloadPdf(doc, `${title.replace(/\s+/g, "_")}_signed_${cse.title.replace(/\s+/g, "_")}.pdf`);
                return;
            }
            setPendingExport({ title, body: content, meta, modeKey });
            setSigOpen(true);
        } else {
            const doc = generateLetterPdf({ title, body: content, meta });
            downloadPdf(doc, `${title.replace(/\s+/g, "_")}_${cse.title.replace(/\s+/g, "_")}.pdf`);
        }
    };

    const onSignatureConfirm = async (dataUrl) => {
        if (!pendingExport) return;
        // Save this signature for future exports (user drew it once, keep it)
        try { await api.put("/user/signature", { data_url: dataUrl }); setSavedSignature(dataUrl); } catch (_) {}
        const doc = generateLetterPdf({ ...pendingExport, signatureDataUrl: dataUrl });
        downloadPdf(doc, `${pendingExport.title.replace(/\s+/g, "_")}_signed_${cse.title.replace(/\s+/g, "_")}.pdf`);
        setPendingExport(null);
    };

    // ----- Court record handlers -----
    const saveCourt = async () => {
        setSavingCourt(true);
        try {
            await api.patch(`/cases/${id}/court-record`, court);
            toast.success("Court record saved");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSavingCourt(false); }
    };

    const explainRecord = async () => {
        if (!court.raw_text.trim()) { toast.error("Paste the record text first"); return; }
        setExplaining(true);
        setExplanation("");
        try {
            const { data } = await api.post("/court-record/explain", { case_id: id, text: court.raw_text });
            setExplanation(data.explanation);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setExplaining(false); }
    };

    // ----- Contacts handlers -----
    const addContact = async (e) => {
        e.preventDefault();
        if (!contactForm.name.trim()) return;
        setSavingContact(true);
        try {
            await api.post(`/cases/${id}/contacts`, contactForm);
            toast.success("Contact added");
            setContactForm({ role: "creditor", name: "", organization: "", address: "", phone: "", email: "", bar_number: "", notes: "" });
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSavingContact(false); }
    };

    const deleteContact = async (cid) => {
        try {
            await api.delete(`/cases/${id}/contacts/${cid}`);
            toast.success("Contact removed");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const runResearch = async () => {
        setResearching(true);
        setResearchOut("");
        try {
            const { data } = await api.post("/contacts/research", {
                case_id: id, target_role: researchTarget, known_info: researchKnown || null,
            });
            setResearchOut(data.strategy);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setResearching(false); }
    };

    // ----- Coach handlers -----
    const sendCoach = async () => {
        if (!coachInput.trim()) return;
        const msg = coachInput.trim();
        setCoachInput("");
        setCoachLoading(true);
        setCoachMessages((m) => [...m, { id: `tmp-${Date.now()}`, role: "user", content: msg, at: new Date().toISOString() }]);
        try {
            const { data } = await api.post("/coach/chat", { case_id: id, message: msg });
            // replace optimistic message with real two
            setCoachMessages((m) => {
                const without = m.filter((x) => !x.id.startsWith("tmp-"));
                return [...without, ...data.messages];
            });
        } catch (e) {
            toast.error(formatApiError(e));
            setCoachMessages((m) => m.filter((x) => !x.id.startsWith("tmp-")));
        } finally { setCoachLoading(false); }
    };

    const resetCoach = async () => {
        try {
            await api.delete(`/coach/messages/${id}`);
            setCoachMessages([]);
            toast.success("Conversation reset");
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const copy = (text) => {
        navigator.clipboard.writeText(text);
        toast.success("Copied to clipboard");
    };

    if (loading) return <div className="text-slate-500">Loading…</div>;
    if (!cse) return <div className="text-slate-500">Case not found.</div>;

    return (
        <div className="space-y-8" data-testid="case-detail">
            <Link to="/cases" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900">
                <ArrowLeft size={14} /> Back to cases
            </Link>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
                <div className="lg:col-span-2 space-y-2">
                    <p className="overline uppercase">{cse.case_type}</p>
                    <h1 className="font-serif text-4xl sm:text-5xl leading-tight">{cse.title}</h1>
                    <p className="text-slate-600">vs. <span className="font-medium text-slate-900">{cse.creditor_name}</span> {cse.case_number ? `· #${cse.case_number}` : ""}</p>
                </div>
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs">Status</Label>
                        <Select value={cse.status} onValueChange={updateStatus}>
                            <SelectTrigger data-testid="status-select" className="mt-1.5 h-10"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {STATUS_LIST.map((s) => <SelectItem key={s} value={s}>{s[0].toUpperCase()+s.slice(1)}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
            </div>

            {/* Quick facts grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Fact label="Amount owed" value={`$${Number(cse.amount_owed).toLocaleString()}`} tint="blue" />
                <Fact label="Original amount" value={cse.original_amount ? `$${Number(cse.original_amount).toLocaleString()}` : "—"} tint="green" />
                <Fact label="Jurisdiction" value={cse.jurisdiction || "—"} tint="yellow" />
                <Fact label="Deadline" value={formatDateUS(cse.deadline) || "—"} tint="red" />
            </div>

            <CaseAddressPrompt caseData={cse} onUpdated={load} />

            {payPolling && (
                <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-900" data-testid="payment-confirming">
                    <span className="animate-spin inline-block h-3 w-3 border-2 border-blue-600 border-t-transparent rounded-full" />
                    Confirming your payment with Stripe — this usually takes a few seconds.
                </div>
            )}

            <AutoEscalationToggle caseData={cse} onUpdated={load} />

            <div className="p-4 bg-white border border-slate-200 rounded-md flex items-center gap-4 flex-wrap" data-testid="signature-manager">
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900">Your signature</p>
                    <p className="text-xs text-slate-500 mt-0.5">
                        Draw once — auto-applied to every signed PDF export on any letter. Currently: {savedSignature ? <span className="text-emerald-700 font-medium">saved</span> : <span className="text-slate-500">not saved</span>}.
                    </p>
                </div>
                {savedSignature ? (
                    <>
                        <img src={savedSignature} alt="Saved signature" className="h-12 border border-slate-200 rounded bg-white" data-testid="signature-preview" />
                        <Button variant="outline" size="sm" onClick={() => setSigManagerOpen(true)} data-testid="signature-change">Change</Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                            onClick={async () => {
                                if (!window.confirm("Clear saved signature?")) return;
                                try { await api.delete("/user/signature"); setSavedSignature(null); toast.success("Signature cleared"); }
                                catch (e) { toast.error(formatApiError(e)); }
                            }}
                            data-testid="signature-saved-clear"
                        >Clear</Button>
                    </>
                ) : (
                    <Button size="sm" onClick={() => setSigManagerOpen(true)} data-testid="signature-create">
                        <PencilSimpleLine size={14} className="mr-1.5" /> Create signature
                    </Button>
                )}
            </div>

            {/* Main tabs */}
            <Tabs defaultValue="ai" className="w-full" data-testid="case-tabs">
                <TabsList className="flex flex-wrap h-auto justify-start gap-1.5 bg-transparent p-0">
                    <TabsTrigger value="ai" data-testid="tab-ai" className="bg-sky-100 hover:bg-sky-200 data-[state=active]:bg-sky-500 data-[state=active]:text-white border border-sky-300">Workbench</TabsTrigger>
                    <TabsTrigger value="court" data-testid="tab-court" className="bg-fuchsia-100 hover:bg-fuchsia-200 data-[state=active]:bg-fuchsia-500 data-[state=active]:text-white border border-fuchsia-300">Court Record</TabsTrigger>
                    <TabsTrigger value="contacts" data-testid="tab-contacts" className="bg-emerald-100 hover:bg-emerald-200 data-[state=active]:bg-emerald-500 data-[state=active]:text-white border border-emerald-300">Contacts</TabsTrigger>
                    <TabsTrigger value="payment" data-testid="tab-payment" className="bg-amber-100 hover:bg-amber-200 data-[state=active]:bg-amber-500 data-[state=active]:text-white border border-amber-300">Payment</TabsTrigger>
                    <TabsTrigger value="coach" data-testid="tab-coach" className="bg-violet-100 hover:bg-violet-200 data-[state=active]:bg-violet-500 data-[state=active]:text-white border border-violet-300">Coach</TabsTrigger>
                    <TabsTrigger value="hardship" data-testid="tab-hardship" className="bg-rose-100 hover:bg-rose-200 data-[state=active]:bg-rose-500 data-[state=active]:text-white border border-rose-300">Hardship</TabsTrigger>
                    <TabsTrigger value="docs" data-testid="tab-docs" className="bg-teal-100 hover:bg-teal-200 data-[state=active]:bg-teal-500 data-[state=active]:text-white border border-teal-300">Documents</TabsTrigger>
                    <TabsTrigger value="history" data-testid="tab-history" className="bg-orange-100 hover:bg-orange-200 data-[state=active]:bg-orange-500 data-[state=active]:text-white border border-orange-300">History</TabsTrigger>
                </TabsList>

                <TabsContent value="ai" className="mt-6 space-y-6">
                    <p className="text-xs text-slate-500 italic">
                        We prepare documents at your direction. You sign and send all correspondence.
                    </p>

                    {/* Pay-for-Deletion action */}
                    <Card className="p-6 bg-gradient-to-br from-[#0F1E3D] to-[#1A2C57] text-white border-0" data-testid="pfd-card">
                        <div className="flex items-start justify-between gap-4 flex-wrap">
                            <div>
                                <p className="overline !text-white/60">Direct settlement</p>
                                <h3 className="font-serif text-2xl mt-1">Send a Pay-for-Deletion offer</h3>
                                <p className="text-sm text-white/70 mt-2 max-w-md">
                                    Email a formal offer to the creditor or attorney with a one-click Accept/Decline link. Case status updates the moment they respond.
                                </p>
                            </div>
                            <Button onClick={() => setPfdOpen(true)} data-testid="pfd-open-btn" className="bg-green-500 text-white hover:bg-green-600 h-10 shrink-0">
                                <PaperPlaneTilt size={14} className="mr-1.5" weight="fill" /> Send offer
                            </Button>
                        </div>

                        {offers.length > 0 && (
                            <div className="mt-5 pt-5 border-t border-white/15 space-y-2" data-testid="offers-list">
                                {(() => {
                                    // Build chain tree: root offers (no parent_token), each with children sorted by created_at
                                    const byToken = Object.fromEntries(offers.map((o) => [o.token, o]));
                                    const children = {};
                                    offers.forEach((o) => {
                                        const p = o.parent_token;
                                        if (p && byToken[p]) (children[p] = children[p] || []).push(o);
                                    });
                                    const roots = offers
                                        .filter((o) => !o.parent_token || !byToken[o.parent_token])
                                        .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
                                    const renderNode = (o, depth = 0) => {
                                        const tone = {
                                            sent: "bg-blue-400/15 text-blue-100",
                                            accepted: "bg-emerald-400/15 text-emerald-100",
                                            declined: "bg-rose-400/15 text-rose-100",
                                            countered: "bg-amber-400/15 text-amber-100",
                                            expired: "bg-slate-400/15 text-slate-200",
                                        }[o.status] || "bg-white/10 text-white";
                                        const StatusIcon = { sent: ClockIcon, accepted: CheckCircle, declined: XCircle, countered: ArrowsClockwise }[o.status] || ClockIcon;
                                        const arrow = depth > 0 ? `↳ ` : "";
                                        const direction = o.offer_type === "counter_offer" ? "Counter" : "Offer";
                                        const kids = (children[o.token] || []).sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
                                        return (
                                            <div key={o.token}>
                                                <div
                                                    className="flex items-center gap-3 text-sm py-1"
                                                    style={{ paddingLeft: `${depth * 18}px` }}
                                                    data-testid={`offer-${o.token}`}
                                                >
                                                    <span className="text-white/40 font-mono text-xs">{arrow}</span>
                                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${tone}`}>
                                                        <StatusIcon size={12} weight="fill" /> {o.status}
                                                    </span>
                                                    <span className="font-mono">${o.offer_amount.toLocaleString()}</span>
                                                    <span className="text-white/50 text-xs">{direction}</span>
                                                    <span className="text-white/60 text-xs truncate flex-1">→ {o.recipient_name}</span>
                                                    <a href={o.public_url} target="_blank" rel="noreferrer">
                                                        <Button variant="ghost" size="sm" className="h-7 text-white hover:bg-white/10">
                                                            <Copy size={12} className="mr-1" /> Link
                                                        </Button>
                                                    </a>
                                                </div>
                                                {kids.map((k) => renderNode(k, depth + 1))}
                                            </div>
                                        );
                                    };
                                    return roots.map((r) => renderNode(r));
                                })()}
                            </div>
                        )}
                    </Card>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="ai-modes">
                        {MODES.map((m) => {
                            const Icon = m.icon;
                            const active = mode === m.key;
                            return (
                                <button key={m.key}
                                    data-testid={`ai-mode-${m.key}`}
                                    onClick={() => setMode(m.key)}
                                    className={`text-left p-4 rounded-md border transition-all ${m.border} ${active ? `${m.activeBg} -translate-y-0.5 ring-2 ring-[#0F1E3D]` : m.bg}`}>
                                    <Icon size={20} weight="duotone" className={m.iconColor} />
                                    <p className="font-semibold mt-3 text-sm">{m.title}</p>
                                    <p className="text-xs text-slate-600 mt-1">{m.desc}</p>
                                </button>
                            );
                        })}
                    </div>

                    <Card className="p-6 space-y-4">
                        <div>
                            <Label htmlFor="extra">Additional context (optional)</Label>
                            <Textarea data-testid="ai-extra" id="extra" rows={3} value={extra} onChange={(e) => setExtra(e.target.value)}
                                placeholder="e.g. Debt is 7 years old, no written contract on file, hardship: medical leave" className="mt-1.5" />
                        </div>
                        <label
                            className={`flex items-center gap-2.5 text-sm cursor-pointer select-none ${
                                cse.hardship?.narrative ? "text-slate-700" : "text-slate-400 cursor-not-allowed"
                            }`}
                            data-testid="ai-include-hardship-label"
                        >
                            <input
                                type="checkbox"
                                checked={includeHardship && !!cse.hardship?.narrative}
                                onChange={(e) => setIncludeHardship(e.target.checked)}
                                disabled={!cse.hardship?.narrative}
                                data-testid="ai-include-hardship"
                                className="w-4 h-4 accent-[#0F1E3D]"
                            />
                            <span>
                                Include the saved hardship narrative in this AI generation
                                {!cse.hardship?.narrative && (
                                    <span className="text-xs italic ml-1">(no narrative saved — add one in the Hardship tab)</span>
                                )}
                            </span>
                        </label>
                        <div className="flex items-center gap-3 flex-wrap">
                            <Button data-testid="ai-run-btn" onClick={runAI} disabled={streaming} className="bg-red-600 hover:bg-red-700 text-white h-10">
                                <Sparkle size={16} className="mr-1.5" weight="fill" />
                                {streaming ? "Generating…" : `Generate ${MODES.find(m => m.key === mode).title}`}
                            </Button>
                            {aiText && !streaming && (
                                <>
                                    <Button variant="outline" onClick={() => copy(aiText)} className="h-10">
                                        <Copy size={14} className="mr-1.5" /> Copy
                                    </Button>
                                    <Button
                                        variant="outline"
                                        onClick={() => printText(aiText, `${MODES.find((m) => m.key === mode).title} — ${cse.title}`)}
                                        data-testid="print-btn"
                                        className="h-10"
                                    >
                                        <Printer size={14} className="mr-1.5" /> Print
                                    </Button>
                                    <Button
                                        variant="outline"
                                        onClick={() => {
                                            const title = MODES.find((m) => m.key === mode).title;
                                            downloadDocx(aiText, `${title.replace(/\s+/g, "_")}_${cse.title.replace(/\s+/g, "_")}`, `${title} — ${cse.title}`);
                                        }}
                                        data-testid="export-docx-btn"
                                        className="h-10"
                                    >
                                        <FileDoc size={14} className="mr-1.5" /> Word
                                    </Button>
                                    <Button variant="outline" onClick={() => exportPdf(aiText, mode, false)} data-testid="export-pdf-btn" className="h-10">
                                        <DownloadSimple size={14} className="mr-1.5" /> PDF
                                    </Button>
                                    {mode === "settlement_letter" && (
                                        <Button onClick={() => exportPdf(aiText, mode, true)} data-testid="sign-export-btn" className="bg-[#2563EB] hover:bg-[#1D4ED8] h-10">
                                            <PencilSimpleLine size={14} className="mr-1.5" /> Sign &amp; Export
                                        </Button>
                                    )}
                                </>
                            )}
                        </div>

                        {(aiText || streaming) && (
                            <div className="border border-slate-200 rounded-md bg-slate-50 p-6" data-testid="ai-output">
                                <p className="overline mb-3">Output · Claude Sonnet 4.5</p>
                                <pre className="whitespace-pre-wrap font-sans text-sm text-slate-800 leading-relaxed">
                                    {aiText}{streaming && <span className="inline-block w-2 h-4 bg-slate-700 ml-0.5 animate-pulse align-middle" />}
                                </pre>
                            </div>
                        )}
                    </Card>
                </TabsContent>

                <TabsContent value="court" className="mt-6 space-y-6">
                    <CourtListenerSearch caseId={id} onImported={load} />
                    <div className="flex items-center gap-2">
                        <label htmlFor="cd-state-select" className="text-xs text-slate-600 font-medium uppercase tracking-wider">State court</label>
                        <Select value={cdStateCode} onValueChange={setCdStateCode}>
                            <SelectTrigger id="cd-state-select" className="h-9 w-64" data-testid="cd-state-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {Object.entries(STATE_CONFIG).map(([code, cfg]) => (
                                    <SelectItem key={code} value={code}>{code} — {cfg.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <StateCourtPanel key={cdStateCode} stateCode={cdStateCode} caseId={id} />
                    <Card className="p-6 space-y-4">
                        <div className="flex items-center gap-2">
                            <Gavel size={18} weight="duotone" className="text-[#2563EB]" />
                            <h3 className="font-semibold">Court / public record details</h3>
                        </div>
                        <p className="text-xs text-slate-500">Enter what you've pulled from the court. We'll keep it on the case file.</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                                <Label htmlFor="cn">Court name</Label>
                                <Input data-testid="court-name" id="cn" value={court.court_name} onChange={(e) => setCourt({ ...court, court_name: e.target.value })} className="mt-1.5 h-11" placeholder="e.g. Superior Court of CA, Los Angeles" />
                            </div>
                            <div>
                                <Label htmlFor="cap">Case caption</Label>
                                <Input data-testid="court-caption" id="cap" value={court.case_caption} onChange={(e) => setCourt({ ...court, case_caption: e.target.value })} className="mt-1.5 h-11" placeholder="e.g. Discover Bank v. Garcia" />
                            </div>
                            <div>
                                <Label htmlFor="dk">Docket number</Label>
                                <Input data-testid="court-docket" id="dk" value={court.docket_number} onChange={(e) => setCourt({ ...court, docket_number: e.target.value })} className="mt-1.5 h-11" />
                            </div>
                            <div>
                                <Label htmlFor="fd">Filed date</Label>
                                <DateField data-testid="court-filed" id="fd" value={court.filed_date} onChange={(e) => setCourt({ ...court, filed_date: e.target.value })} />
                            </div>
                            <div>
                                <Label htmlFor="jd">Judge / official</Label>
                                <Input data-testid="court-judge" id="jd" value={court.judge} onChange={(e) => setCourt({ ...court, judge: e.target.value })} className="mt-1.5 h-11" />
                            </div>
                            <div>
                                <Label htmlFor="pt">Parties</Label>
                                <Input data-testid="court-parties" id="pt" value={court.parties} onChange={(e) => setCourt({ ...court, parties: e.target.value })} className="mt-1.5 h-11" placeholder="Plaintiff(s) vs. Defendant(s)" />
                            </div>
                        </div>
                        <div>
                            <Label htmlFor="rt">Pasted record text / docket entries</Label>
                            <Textarea data-testid="court-raw" id="rt" rows={6} value={court.raw_text} onChange={(e) => setCourt({ ...court, raw_text: e.target.value })} className="mt-1.5" placeholder="Paste the legalese here. We'll translate it into plain English." />
                        </div>
                        <div className="flex gap-3 flex-wrap">
                            <Button onClick={saveCourt} disabled={savingCourt} data-testid="court-save-btn" className="bg-[#0F1E3D] hover:bg-[#1A2C57]">
                                {savingCourt ? "Saving…" : "Save record"}
                            </Button>
                            <Button onClick={explainRecord} disabled={explaining || !court.raw_text.trim()} variant="outline" data-testid="court-explain-btn">
                                <Sparkle size={14} className="mr-1.5" weight="fill" />
                                {explaining ? "Translating…" : "Explain in plain English"}
                            </Button>
                        </div>
                        <p className="text-xs text-slate-500 italic">Informational only — we don't interpret your record as legal advice.</p>
                    </Card>

                    {explanation && (
                        <Card className="p-6 bg-blue-50/40 border-blue-200" data-testid="court-explanation">
                            <p className="overline">Plain-English explanation</p>
                            <pre className="whitespace-pre-wrap font-sans text-sm text-slate-800 mt-3 leading-relaxed">{explanation}</pre>
                        </Card>
                    )}
                </TabsContent>

                <TabsContent value="contacts" className="mt-6 space-y-6">
                    <Card className="p-6 space-y-4">
                        <div className="flex items-center gap-2">
                            <MagnifyingGlass size={18} weight="duotone" className="text-[#2563EB]" />
                            <h3 className="font-semibold">Locate creditor or attorney — AI search strategy</h3>
                        </div>
                        <p className="text-xs text-slate-500">Tell us who you're trying to reach. We'll draft a research plan with sources, queries and steps you can run yourself.</p>
                        <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-3">
                            <Select value={researchTarget} onValueChange={setResearchTarget}>
                                <SelectTrigger data-testid="research-target" className="h-11"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="creditor">Creditor</SelectItem>
                                    <SelectItem value="attorney">Attorney</SelectItem>
                                    <SelectItem value="court_clerk">Court clerk</SelectItem>
                                    <SelectItem value="other">Other</SelectItem>
                                </SelectContent>
                            </Select>
                            <Textarea data-testid="research-known" rows={2} value={researchKnown} onChange={(e) => setResearchKnown(e.target.value)} placeholder="What do you already know? (name, partial address, firm, bar #)…" />
                        </div>
                        <Button onClick={runResearch} disabled={researching} data-testid="research-run-btn" className="bg-[#0F1E3D] hover:bg-[#1A2C57]">
                            <Sparkle size={14} className="mr-1.5" weight="fill" />
                            {researching ? "Building strategy…" : "Build search strategy"}
                        </Button>
                        {researchOut && (
                            <div className="border border-slate-200 rounded-md bg-slate-50 p-5" data-testid="research-output">
                                <p className="overline mb-2">Research plan</p>
                                <pre className="whitespace-pre-wrap font-sans text-sm text-slate-800 leading-relaxed">{researchOut}</pre>
                            </div>
                        )}
                    </Card>

                    <Card className="p-6 space-y-4">
                        <div className="flex items-center gap-2">
                            <AddressBook size={18} weight="duotone" className="text-[#2563EB]" />
                            <h3 className="font-semibold">Save a contact</h3>
                        </div>
                        <form onSubmit={addContact} className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="contact-form">
                            <div>
                                <Label>Role</Label>
                                <Select value={contactForm.role} onValueChange={(v) => setContactForm({ ...contactForm, role: v })}>
                                    <SelectTrigger data-testid="contact-role" className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="creditor">Creditor</SelectItem>
                                        <SelectItem value="attorney">Attorney</SelectItem>
                                        <SelectItem value="court_clerk">Court clerk</SelectItem>
                                        <SelectItem value="other">Other</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label htmlFor="kn">Name *</Label>
                                <Input data-testid="contact-name" id="kn" required value={contactForm.name} onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })} className="mt-1.5 h-11" />
                            </div>
                            <div>
                                <Label htmlFor="ko">Organization / firm</Label>
                                <Input data-testid="contact-org" id="ko" value={contactForm.organization} onChange={(e) => setContactForm({ ...contactForm, organization: e.target.value })} className="mt-1.5 h-11" />
                            </div>
                            <div>
                                <Label htmlFor="kp">Phone</Label>
                                <Input
                                    data-testid="contact-phone"
                                    id="kp"
                                    inputMode="tel"
                                    placeholder="(555) 123-4567"
                                    value={formatPhoneUS(contactForm.phone)}
                                    onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value.replace(/\D+/g, "") })}
                                    className="mt-1.5 h-11"
                                />
                            </div>
                            <div>
                                <Label htmlFor="ke">Email</Label>
                                <Input data-testid="contact-email" id="ke" type="email" value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} className="mt-1.5 h-11" />
                            </div>
                            <div>
                                <Label htmlFor="kb">Bar number (attorneys)</Label>
                                <Input data-testid="contact-bar" id="kb" value={contactForm.bar_number} onChange={(e) => setContactForm({ ...contactForm, bar_number: e.target.value })} className="mt-1.5 h-11" />
                            </div>
                            <div className="md:col-span-2">
                                <Label htmlFor="ka">Address</Label>
                                <Input data-testid="contact-address" id="ka" value={contactForm.address} onChange={(e) => setContactForm({ ...contactForm, address: e.target.value })} className="mt-1.5 h-11" />
                            </div>
                            <div className="md:col-span-2">
                                <Label htmlFor="kn2">Notes</Label>
                                <Textarea data-testid="contact-notes" id="kn2" rows={2} value={contactForm.notes} onChange={(e) => setContactForm({ ...contactForm, notes: e.target.value })} className="mt-1.5" />
                            </div>
                            <div className="md:col-span-2">
                                <Button type="submit" disabled={savingContact} data-testid="contact-save-btn" className="bg-[#0F1E3D] hover:bg-[#1A2C57]">
                                    {savingContact ? "Saving…" : "Add contact"}
                                </Button>
                            </div>
                        </form>
                    </Card>

                    <div className="space-y-3" data-testid="contacts-list">
                        {(cse.contacts || []).length === 0 ? (
                            <p className="text-sm text-slate-500">No contacts saved yet.</p>
                        ) : cse.contacts.map((k) => (
                            <Card key={k.id} className="p-5" data-testid={`contact-${k.id}`}>
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700 capitalize">{k.role.replace("_"," ")}</span>
                                            <p className="font-semibold">{k.name}</p>
                                        </div>
                                        <p className="text-xs text-slate-500 mt-1">{[k.organization, k.bar_number && `Bar #${k.bar_number}`].filter(Boolean).join(" · ")}</p>
                                        <p className="text-sm text-slate-600 mt-2">{[displayPhone(k.phone), k.email, k.address].filter(Boolean).join(" · ")}</p>
                                        {k.notes && <p className="text-xs text-slate-500 mt-2 italic">{k.notes}</p>}
                                    </div>
                                    <Button variant="ghost" size="sm" onClick={() => deleteContact(k.id)} data-testid={`contact-del-${k.id}`}><Trash size={14} /></Button>
                                </div>
                            </Card>
                        ))}
                    </div>
                </TabsContent>

                <TabsContent value="payment" className="mt-6 space-y-6" data-testid="payment-panel">
                    <PaymentInstructionsEditor
                        caseId={id}
                        initial={cse.payment_instructions}
                        caseStatus={cse.status}
                        amountOwed={cse.amount_owed}
                        onSaved={() => load()}
                    />
                    {cse.payment_instructions && Object.values(cse.payment_instructions).some(Boolean) && (
                        <PaymentInstructionsView
                            instructions={cse.payment_instructions}
                            amount={cse.amount_owed || 0}
                        />
                    )}
                </TabsContent>

                <TabsContent value="coach" className="mt-6 space-y-4">
                    <Card className="p-6 space-y-3" data-testid="coach-panel">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                            <div className="flex items-center gap-2">
                                <ChatCircleDots size={18} weight="duotone" className="text-[#2563EB]" />
                                <h3 className="font-semibold">Negotiation coach &mdash; practice with a role-playing creditor</h3>
                            </div>
                            {coachMessages.length > 0 && (
                                <Button variant="ghost" size="sm" onClick={resetCoach} data-testid="coach-reset-btn">
                                    <ArrowsClockwise size={14} className="mr-1.5" /> Reset
                                </Button>
                            )}
                        </div>
                        <p className="text-xs text-slate-500 italic">
                            This is a simulator. Coach feedback alternates with creditor role-play. Practise your scripts here before real calls. Not legal advice.
                        </p>

                        <div className="border border-slate-200 rounded-md bg-slate-50 p-4 max-h-[480px] overflow-y-auto space-y-3" data-testid="coach-messages">
                            {coachMessages.length === 0 ? (
                                <p className="text-sm text-slate-500 text-center py-8">
                                    Start by introducing yourself as if you've just dialed the creditor. The coach will jump in.
                                </p>
                            ) : coachMessages.map((m) => (
                                <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                                    <div className={`max-w-[80%] rounded-md px-4 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-[#0F1E3D] text-white" : "bg-white border border-slate-200 text-slate-800"}`}>
                                        <pre className="whitespace-pre-wrap font-sans">{m.content}</pre>
                                    </div>
                                </div>
                            ))}
                            {coachLoading && <p className="text-xs text-slate-500 italic">Coach is thinking…</p>}
                        </div>

                        <div className="flex gap-2">
                            <Input
                                value={coachInput}
                                onChange={(e) => setCoachInput(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendCoach(); } }}
                                placeholder="Type what you'd say to the creditor… (Enter to send)"
                                disabled={coachLoading}
                                className="h-11"
                                data-testid="coach-input"
                            />
                            <Button onClick={sendCoach} disabled={coachLoading || !coachInput.trim()} data-testid="coach-send-btn" className="bg-[#0F1E3D] hover:bg-[#1A2C57] h-11">
                                <PaperPlaneTilt size={16} />
                            </Button>
                        </div>
                    </Card>
                </TabsContent>

                <TabsContent value="hardship" className="mt-6">
                    <HardshipWorksheet caseId={id} initialData={cse.hardship} onSaved={load} />
                </TabsContent>

                <TabsContent value="docs" className="mt-6 space-y-6">
                    <Card className="p-6">
                        <h3 className="font-semibold">Upload a PDF or image — auto-extracted via Gemini OCR</h3>
                        <p className="text-xs text-slate-500 mt-1">PDF, PNG, JPG up to 15MB. Text is extracted and a "Key Facts" summary is generated automatically.</p>
                        <div className="mt-4 flex items-center gap-3" data-testid="file-upload-block">
                            <Input
                                type="file"
                                ref={fileRef}
                                accept=".pdf,.png,.jpg,.jpeg,.webp,.txt,.csv"
                                onChange={(e) => uploadFile(e.target.files?.[0])}
                                disabled={uploading}
                                className="h-11"
                                data-testid="doc-upload-input"
                            />
                            {uploading && <span className="text-xs text-slate-500 inline-flex items-center gap-1.5"><UploadSimple size={14} weight="duotone" /> Extracting…</span>}
                        </div>
                    </Card>
                    <Card className="p-6">
                        <h3 className="font-semibold">Or paste a note manually</h3>
                        <form onSubmit={addDocument} className="mt-4 grid grid-cols-1 gap-3" data-testid="doc-form">
                            <Input data-testid="doc-name" value={docName} onChange={(e) => setDocName(e.target.value)} placeholder="Document name (e.g. Collection notice 2025-12)" className="h-11" />
                            <Textarea data-testid="doc-content" rows={5} value={docContent} onChange={(e) => setDocContent(e.target.value)} placeholder="Paste the document text…" />
                            <div>
                                <Button data-testid="doc-add-btn" type="submit" className="bg-[#0F1E3D] hover:bg-[#1A2C57]">
                                    <Paperclip size={14} className="mr-1.5" /> Attach note
                                </Button>
                            </div>
                        </form>
                    </Card>
                    <div className="space-y-3">
                        {(cse.documents || []).length === 0 ? (
                            <p className="text-sm text-slate-500">No documents yet.</p>
                        ) : cse.documents.map((d) => (
                            <Card key={d.id} className="p-5" data-testid={`doc-item-${d.id}`}>
                                <div className="flex items-center justify-between gap-3">
                                    <div className="min-w-0">
                                        <p className="font-semibold text-sm truncate">{d.name}</p>
                                        <p className="text-xs text-slate-500 mt-0.5">
                                            {d.mime ? `${d.mime} · ` : ""}
                                            {d.size_bytes ? `${Math.round(d.size_bytes / 1024)} KB · ` : ""}
                                            {formatDateUS(d.uploaded_at)}
                                        </p>
                                    </div>
                                </div>
                                <pre className="text-sm text-slate-600 mt-3 line-clamp-6 whitespace-pre-wrap font-sans">{d.content}</pre>
                            </Card>
                        ))}
                    </div>
                </TabsContent>

                <TabsContent value="history" className="mt-6 space-y-6">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <h3 className="font-semibold">AI outputs</h3>
                        {(cse.ai_outputs || []).length > 1 && (
                            <Button
                                variant="outline"
                                size="sm"
                                data-testid="export-all-docx-btn"
                                onClick={() => {
                                    const sections = [...cse.ai_outputs].reverse().map((o) => ({
                                        title: `${MODE_TITLES[o.mode] || o.mode} — ${new Date(o.created_at).toLocaleDateString()}`,
                                        content: o.content,
                                    }));
                                    downloadDocxMulti(sections, `AI_Outputs_${cse.title.replace(/\s+/g, "_")}`);
                                }}
                            >
                                <FileDoc size={14} className="mr-1.5" /> Download all as Word
                            </Button>
                        )}
                    </div>
                    {(cse.ai_outputs || []).length === 0 ? (
                        <p className="text-sm text-slate-500">No AI outputs yet. Run the workbench to generate one.</p>
                    ) : [...cse.ai_outputs].reverse().map((o) => (
                        <Card key={o.id} className="p-6 bg-white" data-testid={`history-${o.id}`}>
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <p className="overline">{o.mode.replace(/_/g, " ")}</p>
                                    {o.edited_at && (
                                        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px] gap-1" data-testid={`history-edited-${o.id}`}>
                                            Edited
                                        </Badge>
                                    )}
                                </div>
                                <div className="flex items-center gap-2">
                                    <p className="text-xs text-slate-500">{new Date(o.created_at).toLocaleString()}</p>
                                    {(o.mode === "dispute_letter" || o.mode === "settlement_letter") && (
                                        <Button variant="ghost" size="sm" onClick={() => openEditDialog(o)} title="Edit letter" data-testid={`history-edit-${o.id}`}><PencilSimpleLine size={14} /></Button>
                                    )}
                                    <Button variant="ghost" size="sm" onClick={() => copy(o.content)} title="Copy"><Copy size={14} /></Button>
                                    <Button variant="ghost" size="sm" onClick={() => printText(o.content, `${MODE_TITLES[o.mode] || o.mode} — ${cse.title}`)} title="Print" data-testid={`history-print-${o.id}`}><Printer size={14} /></Button>
                                    <Button variant="ghost" size="sm" onClick={() => downloadDocx(o.content, `${(MODE_TITLES[o.mode] || o.mode).replace(/\s+/g, "_")}_${cse.title.replace(/\s+/g, "_")}`, `${MODE_TITLES[o.mode] || o.mode} — ${cse.title}`)} title="Save as Word" data-testid={`history-docx-${o.id}`}><FileDoc size={14} /></Button>
                                    <Button variant="ghost" size="sm" onClick={() => exportPdf(o.content, o.mode, false)} title="Export PDF" data-testid={`history-export-${o.id}`}><DownloadSimple size={14} /></Button>
                                    {(o.mode === "settlement_letter" || o.mode === "dispute_letter") && (
                                        <Button variant="ghost" size="sm" onClick={() => exportPdf(o.content, o.mode, true)} title={savedSignature ? "Export signed PDF (uses saved signature)" : "Sign & Export"} data-testid={`history-sign-${o.id}`}><Signature size={14} weight="fill" /></Button>
                                    )}
                                    {(o.mode === "dispute_letter" || o.mode === "settlement_letter") && (
                                        <Button variant="ghost" size="sm" onClick={() => openMailDialog(o)} title="Mail via USPS" data-testid={`history-mail-${o.id}`} className="text-[#0F1E3D]">
                                            <PaperPlaneTilt size={14} weight="fill" />
                                        </Button>
                                    )}
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => regenerate(o)}
                                        disabled={regenId === o.id}
                                        title="Regenerate with AI"
                                        data-testid={`history-regen-${o.id}`}
                                        className="text-slate-600"
                                    >
                                        <ArrowsClockwise size={14} className={regenId === o.id ? "animate-spin" : ""} />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setDeleteOutput(o)}
                                        title="Delete letter"
                                        data-testid={`history-delete-${o.id}`}
                                        className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                                    >
                                        <Trash size={14} />
                                    </Button>
                                </div>
                            </div>
                            {mailings.filter((m) => m.ai_output_id === o.id).map((m) => (
                                <div key={m.id} className={`mt-3 flex items-center gap-2 flex-wrap text-xs border rounded px-3 py-2 ${m.refund_status ? "bg-rose-50 border-rose-200" : "bg-slate-50 border-slate-200"}`} data-testid={`mailing-${m.id}`}>
                                    {m.refund_status ? (
                                        <Badge variant="outline" className="bg-rose-100 text-rose-800 border-rose-300 gap-1">
                                            <ArrowsClockwise size={11} weight="fill" /> refunded ({m.refund_status.replace(/_/g, " ")})
                                        </Badge>
                                    ) : (
                                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 gap-1">
                                            <PaperPlaneTilt size={11} weight="fill" /> {m.status.replace(/_/g, " ")}
                                        </Badge>
                                    )}
                                    {m.environment === "test" && <span className="text-amber-700 uppercase tracking-wider">sandbox</span>}
                                    <span className="text-slate-600">{m.postage_class.replace(/_/g, " ")}</span>
                                    {m.tracking_number && (
                                        <span className="text-slate-500">· Tracking <span className="font-mono">{m.tracking_number}</span></span>
                                    )}
                                    {m.expected_delivery_date && (
                                        <span className="text-slate-500">· ETA {new Date(m.expected_delivery_date).toLocaleDateString()}</span>
                                    )}
                                    <span className="text-slate-400">· ~${m.cost_estimate_usd?.toFixed(2)}</span>
                                    {!m.refund_status && ["queued", "created", "mailed", "in_transit"].includes(m.status) && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => refundMailing(m)}
                                            className="ml-auto h-6 text-rose-700 hover:text-rose-800 hover:bg-rose-100 text-[11px]"
                                            data-testid={`refund-mailing-${m.id}`}
                                        >
                                            <ArrowsClockwise size={11} className="mr-1" /> Refund
                                        </Button>
                                    )}
                                    {m.refund_status && m.refund_reason && (
                                        <span className="text-rose-700 italic w-full mt-1 text-[11px]">Reason: {m.refund_reason}</span>
                                    )}
                                </div>
                            ))}
                            <pre className="whitespace-pre-wrap font-sans text-sm text-slate-700 mt-3 leading-relaxed">{o.content}</pre>
                        </Card>
                    ))}

                    <Card className="p-5" data-testid="audit-log">
                        <h3 className="font-semibold mb-3 flex items-center gap-2">
                            <ClockIcon size={16} weight="duotone" className="text-[#2563EB]" />
                            Audit log
                        </h3>
                        {auditLog.length === 0 ? (
                            <p className="text-sm text-slate-500">No events recorded yet.</p>
                        ) : (
                            <ol className="relative border-l border-slate-200 ml-2 space-y-3">
                                {auditLog.map((e) => (
                                    <li key={e.id} className="ml-4" data-testid={`audit-${e.id}`}>
                                        <span className="absolute -left-1.5 w-3 h-3 bg-[#2563EB] rounded-full mt-1.5" />
                                        <p className="text-sm font-medium text-slate-800">
                                            {e.action.replace(/[._]/g, " ")}
                                            {e.actor === "recipient" && <span className="ml-2 text-xs uppercase tracking-wider text-amber-700">recipient</span>}
                                        </p>
                                        <p className="text-xs text-slate-500">{new Date(e.at).toLocaleString()}</p>
                                        {e.details && Object.keys(e.details).length > 0 && (
                                            <pre className="text-xs text-slate-600 mt-1 font-mono whitespace-pre-wrap">{JSON.stringify(e.details)}</pre>
                                        )}
                                    </li>
                                ))}
                            </ol>
                        )}
                    </Card>
                </TabsContent>
            </Tabs>

            <SignatureModal open={sigOpen} onOpenChange={setSigOpen} onConfirm={onSignatureConfirm} />
            <SignatureModal
                open={sigManagerOpen}
                onOpenChange={setSigManagerOpen}
                onConfirm={async (dataUrl) => {
                    try {
                        await api.put("/user/signature", { data_url: dataUrl });
                        setSavedSignature(dataUrl);
                        toast.success("Signature saved — will apply to every signed export");
                    } catch (e) { toast.error(formatApiError(e)); }
                }}
            />
            <PayForDeletionModal open={pfdOpen} onOpenChange={setPfdOpen} caseData={cse} onSent={loadOffers} />
            <MailLetterDialog
                open={mailOpen}
                onOpenChange={setMailOpen}
                caseData={cse}
                aiOutput={mailingOutput}
                userName={cse?.client_name || undefined}
                onSent={loadMailings}
            />
            <EditAIOutputDialog
                open={editOpen}
                onOpenChange={setEditOpen}
                caseId={id}
                aiOutput={editOutput}
                onSaved={load}
            />
            <AlertDialog open={!!deleteOutput} onOpenChange={(o) => !o && setDeleteOutput(null)}>
                <AlertDialogContent data-testid="delete-ai-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this letter?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Permanently removes this AI-generated {deleteOutput?.mode?.replace(/_/g, " ") || "output"} from the case.
                            Prior mailings, edits history, and audit-log entries remain untouched.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={deletingOutput}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmDeleteOutput}
                            disabled={deletingOutput}
                            className="bg-rose-600 hover:bg-rose-700 text-white"
                            data-testid="delete-ai-confirm"
                        >
                            {deletingOutput ? "Deleting…" : "Delete letter"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <CaseAuditTimeline caseId={id} />
        </div>
    );
}

function Fact({ label, value, tint }) {
    const tints = {
        blue: { bg: "bg-blue-100", border: "border-blue-300" },
        green: { bg: "bg-emerald-100", border: "border-emerald-300" },
        yellow: { bg: "bg-amber-100", border: "border-amber-300" },
        red: { bg: "bg-rose-100", border: "border-rose-300" },
    };
    const t = tints[tint] || { bg: "bg-white", border: "border-slate-200" };
    return (
        <div className={`${t.bg} ${t.border} border rounded-md p-5`}>
            <p className="overline">{label}</p>
            <p className="mt-1.5 text-lg font-medium font-mono break-words">{value}</p>
        </div>
    );
}
